import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  CircularProgress
} from '@material-ui/core';
import { Badge, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import { styled } from '@material-ui/core/styles';

import NotificationCard from './NotificationCard';
import { formatNotificationTime, sanitizeHTML } from '../../apis/Utils';
import {
  GetAppNotifications,
  AppNotificationMarkAsRead
} from '../../apis/Agency';
import NotificationDialog from './NotificationDialog';
import { MetaTags } from 'react-meta-tags';
import { base_url } from '../../apis/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setUserAppNotifications } from '../../Redux/UserSlice';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// Styled components
const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: '#f5f7fb'
}));

const HeaderSection = styled(Box)({
  width: '80%',
  margin: '0 auto 24px auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const PageTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '28px',
  color: '#1e293b'
});

const MarkAllButton = styled(Button)({
  borderRadius: '12px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: 'white',
  color: '#2563eb',
  border: '1px solid #e2e8f0',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#2563eb'
  },
  '&.Mui-disabled': {
    backgroundColor: '#f1f5f9',
    color: '#94a3b8'
  }
});

const ColumnHeader = styled(Box)(({ bgColor }) => ({
  padding: '16px 20px',
  background: bgColor || 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
  borderRadius: '16px',
  marginBottom: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const ColumnTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '18px',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

const CountBadge = styled('span')({
  backgroundColor: 'rgba(255,255,255,0.2)',
  padding: '4px 10px',
  borderRadius: '20px',
  fontSize: '13px',
  fontWeight: 600,
  color: 'white',
  marginLeft: '8px'
});

const NotificationsColumn = styled(Paper)({
  flex: 1,
  backgroundColor: 'white',
  borderRadius: '24px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 180px)',
  overflow: 'hidden',
  padding: '20px'
});

const NotificationsList = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  paddingRight: '8px',
  '&::-webkit-scrollbar': {
    width: '5px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f5f9',
    borderRadius: '10px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#cbd5e1',
    borderRadius: '10px',
    '&:hover': {
      background: '#94a3b8'
    }
  }
});

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#94a3b8',
  textAlign: 'center'
});

function Notifications() {
  const dispatch = useDispatch();
  const currentNotifications = useSelector(
    state => state?.user?.UserAppNotifications?.notifications
  );
  const [readNotifications, setReadNotifications] = useState([]);
  const [unreadNotifications, setUnReadNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [open, setOpen] = useState(false);
  const [notifView, setNotifView] = useState({});

  const handleClose = () => {
    setOpen(false);
  };

  const fetchNotifications = () => {
    setLoading(true);
    GetAppNotifications(false, 0)
      .then(res => {
        dispatch(
          setUserAppNotifications({
            notifications: currentNotifications,
            count: res?.data?.pagingInfo?.pageSize
          })
        );
        setReadNotifications(res?.data?.data?.filter(notif => notif.isRead));
        setUnReadNotifications(res?.data?.data?.filter(notif => !notif.isRead));
        setLoading(false);
      })
      .catch(err => {
        console.log(err, 'appNotificationError');
        setLoading(false);
      });
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = unreadNotifications.map(n => n.id);
    if (unreadIds.length === 0) return;

    setMarkingAllAsRead(true);

    try {
      // Process all unread notifications
      for (let i = 0; i < unreadIds.length; i++) {
        await AppNotificationMarkAsRead(unreadIds[i]);
      }

      // Refresh the list after all are marked as read
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const Loader = () => {
    return [1, 2, 3, 4].map((v, index) => (
      <Skeleton
        key={index}
        variant='rounded'
        height={100}
        sx={{
          mb: 2,
          borderRadius: '16px',
          backgroundColor: '#f1f5f9'
        }}
      />
    ));
  };

  const Notiffs = ({ notifarray, type }) => {
    if (!notifarray?.length) {
      return (
        <EmptyState>
          <Typography
            variant='h6'
            style={{ fontWeight: 500, color: '#1e293b', marginBottom: '8px' }}
          >
            {type === 'new' ? '✨ All caught up!' : '📭 No older notifications'}
          </Typography>
          <Typography variant='body2' style={{ color: '#64748b' }}>
            {type === 'new'
              ? 'You have no unread notifications'
              : 'Your read notifications will appear here'}
          </Typography>
        </EmptyState>
      );
    }

    return (
      <NotificationsList>
        {notifarray?.map((notiff, index) => (
          <Box
            key={notiff.id}
            sx={{ mb: index < notifarray.length - 1 ? 2 : 0 }}
          >
            <NotificationCard
              id={notiff.id}
              indicator={false}
              titleLength={50}
              status={!notiff.isRead}
              title={notiff.title}
              description={sanitizeHTML(notiff.message)}
              time={formatNotificationTime(notiff.receivedOn)}
              setGlobalOpen={setOpen}
              setGlobalNotification={setNotifView}
              reFetch={fetchNotifications}
            />
          </Box>
        ))}
      </NotificationsList>
    );
  };

  return (
    <Container>
      <MetaTags>
        <title>
          {base_url === 'https://beta.api.shiptechpro.com/'
            ? 'Notifications | Shiptechpro'
            : 'Notifications | TMS Shipping'}
        </title>
      </MetaTags>

      <NotificationDialog
        notifView={notifView}
        isOpen={open}
        handleClose={handleClose}
      />

      <Box className='d-flex gap-4' style={{ width: '80%', margin: '0 auto' }}>
        {/* New Notifications Column */}
        <NotificationsColumn>
          <ColumnHeader bgColor='linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'>
            <ColumnTitle>
              <span>🔔</span> New
              {unreadNotifications?.length > 0 && (
                <CountBadge>{unreadNotifications.length}</CountBadge>
              )}
            </ColumnTitle>
            {unreadNotifications?.length > 0 && (
              <Typography
                variant='caption'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                {unreadNotifications.length} unread
              </Typography>
            )}
          </ColumnHeader>

          {loading && !open ? (
            <Loader />
          ) : (
            <Notiffs notifarray={unreadNotifications} type='new' />
          )}
        </NotificationsColumn>

        {/* Divider */}
        <Box style={{ position: 'relative', width: '20px' }}>
          <Divider
            orientation='vertical'
            flexItem
            style={{
              backgroundColor: '#e2e8f0',
              height: '100%',
              margin: '0 auto'
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              color: '#94a3b8',
              border: '1px solid #e2e8f0'
            }}
          >
            ●
          </div>
        </Box>

        {/* Read Notifications Column */}
        <NotificationsColumn>
          <ColumnHeader bgColor='linear-gradient(135deg, #475569 0%, #1e293b 100%)'>
            <ColumnTitle>
              <span>📋</span> Earlier
              {readNotifications?.length > 0 && (
                <CountBadge>{readNotifications.length}</CountBadge>
              )}
            </ColumnTitle>
          </ColumnHeader>

          {loading && !open ? (
            <Loader />
          ) : (
            <Notiffs notifarray={readNotifications} type='earlier' />
          )}
        </NotificationsColumn>
      </Box>
    </Container>
  );
}

export default Notifications;
